import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { headerOffset } from "components/basic/header";
import Typography from "components/basic/typography";
import ChargerImage from "resources/images/ChargerImage.png";
import Button from "components/basic/button";
import { useNavigate } from "react-router-dom";
import { useSnack } from "hooks/useSnack";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import GooglePlacesSearch from "components/basic/googlePlacesSearch";
import logger from "utils/logger";

const AddressPage: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const showSnack = useSnack();

  const handleNext = () => {
    if (!chargerEstimatorData.address?.trim()) {
      showSnack(t("address.invalid"), "error");
      return;
    }

    // Installers are matched to the address by distance, so we need the
    // coordinates that only come with a picked suggestion (typing clears them).
    if (
      chargerEstimatorData.latitude == null ||
      chargerEstimatorData.longitude == null
    ) {
      showSnack(t("address.pick_suggestion"), "error");
      return;
    }

    navigate("?currentPage=chargerLocation");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "auto", md: `calc(100dvh - ${headerOffset(1)})` },
      }}
    >
      <Box
        sx={{
          flex: 1,
          // Without this a flex child refuses to shrink below its content,
          // which is what let the fixed-width search box widen the page.
          minWidth: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: { xs: "1.5rem 1rem", md: "2rem" },
          textAlign: { xs: "center", md: "right" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
            width: "100%",
            maxWidth: "390px",
          }}
        >
          <Typography
            style={{ fontWeight: "bold" }}
            sx={{ fontSize: "clamp(2rem, 7vw, 4rem)" }}
            variant="h2"
            align="inherit"
          >
            {t("address.title")}
          </Typography>

          <Typography variant="body2" align="inherit">
            {t("address.intro")}
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
            }}
          >
            <GooglePlacesSearch
              name="address"
              textAlign={isCompact ? "left" : "right"}
              size="tiny"
              style={{ width: "100%", maxWidth: "350px" }}
              placeHolder={t("address.placeholder")}
              value={chargerEstimatorData.address || ""}
              onInputChange={(text) => {
                const updatedData = {
                  ...chargerEstimatorData,
                  address: text,
                  latitude: undefined,
                  longitude: undefined,
                  placeId: undefined,
                };
                logger.log("AddressPage - onInputChange:", updatedData);
                setChargerEstimatorData(updatedData);
              }}
              onSelect={(place) => {
                const updatedData = {
                  ...chargerEstimatorData,
                  address: place.address,
                  latitude: place.latitude,
                  longitude: place.longitude,
                  placeId: place.placeId,
                };
                logger.log("AddressPage - onSelect:", updatedData);
                setChargerEstimatorData(updatedData);
              }}
            />
          </Box>

          <Box>
            <Button
              style={{
                color: theme.palette.common.white,
                fontWeight: "bold",
              }}
              sx={{
                fontSize: "1.2em",
                px: 3.5,
                width: { xs: "100%", sm: "auto" },
              }}
              variant="contained"
              color="warning"
              onClick={handleNext}
            >
              {t("nav.next")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          alignContent: "center",
          zIndex: -1,
          display: { xs: "none", md: "block" },
        }}
      >
        <img
          src={ChargerImage}
          alt={t("address.image_alt")}
          style={{
            width: "75%",
            height: "75%",
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
  );
};

export default AddressPage;
